import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lock, Eye, EyeOff, Shield, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/axios';

const ForceChangePassword = () => {
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 6) errors.push('Mínimo 6 caracteres');
    if (!/[A-Z]/.test(password)) errors.push('Al menos una mayúscula');
    if (!/[0-9]/.test(password)) errors.push('Al menos un número');
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validaciones
    const passwordErrors = validatePassword(newPassword);
    if (passwordErrors.length > 0) {
      setErrors({ password: passwordErrors });
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setErrors({ confirm: ['Las contraseñas no coinciden'] });
      return;
    }
    
    setErrors({});
    setLoading(true);
    
    try {
      await api.post('/auth/force-change-password', {
        new_password: newPassword
      });
      
      // Actualizar el usuario en localStorage para quitar el flag
      const userData = localStorage.getItem('user');
      if (userData) {
        const user = JSON.parse(userData);
        user.must_change_password = false;
        localStorage.setItem('user', JSON.stringify(user));
      }
      
      toast.success('Contraseña actualizada correctamente');
      navigate('/');
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Error al cambiar la contraseña');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-white/95 backdrop-blur shadow-2xl">
        <CardHeader className="text-center space-y-2">
          <div className="mx-auto w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-2">
            <Shield className="w-8 h-8 text-amber-600" />
          </div>
          <CardTitle className="text-2xl font-bold text-slate-800">
            Cambio de Contraseña Requerido
          </CardTitle>
          <CardDescription className="text-slate-600">
            Por seguridad, debes cambiar tu contraseña temporal antes de continuar.
          </CardDescription>
        </CardHeader>
        
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            {/* Alerta informativa */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-medium">Tu contraseña actual es temporal</p>
                <p className="text-amber-700 mt-1">
                  Crea una nueva contraseña segura para proteger tu cuenta.
                </p>
              </div>
            </div>
            
            {/* Nueva contraseña */}
            <div className="space-y-2">
              <Label htmlFor="newPassword" className="text-slate-700">
                Nueva Contraseña
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="newPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Ingresa tu nueva contraseña"
                  className="pl-10 pr-10"
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {errors.password && (
                <ul className="text-xs text-red-500 mt-1 space-y-0.5">
                  {errors.password.map((err, i) => (
                    <li key={err}>• {err}</li>
                  ))}
                </ul>
              )}
            </div>
            
            {/* Confirmar contraseña */}
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-slate-700">
                Confirmar Contraseña
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="confirmPassword"
                  type={showConfirm ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirma tu nueva contraseña"
                  className="pl-10 pr-10"
                  data-testid="confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {errors.confirm && (
                <ul className="text-xs text-red-500 mt-1">
                  {errors.confirm.map((err, i) => (
                    <li key={err}>• {err}</li>
                  ))}
                </ul>
              )}
            </div>
            
            {/* Requisitos */}
            <div className="text-xs text-slate-500 bg-slate-50 rounded-lg p-3">
              <p className="font-medium mb-1">La contraseña debe tener:</p>
              <ul className="space-y-0.5">
                <li className={newPassword.length >= 6 ? 'text-green-600' : ''}>
                  • Mínimo 6 caracteres
                </li>
                <li className={/[A-Z]/.test(newPassword) ? 'text-green-600' : ''}>
                  • Al menos una letra mayúscula
                </li>
                <li className={/[0-9]/.test(newPassword) ? 'text-green-600' : ''}>
                  • Al menos un número
                </li>
              </ul>
            </div>
          </CardContent>
          
          <CardFooter className="flex flex-col gap-3">
            <Button
              type="submit"
              disabled={loading || !newPassword || !confirmPassword}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="change-password-submit"
            >
              {loading ? 'Guardando...' : 'Cambiar Contraseña'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={handleLogout}
              className="w-full text-slate-500 hover:text-slate-700"
            >
              Cerrar sesión
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
};

export default ForceChangePassword;
